import { isNullOrUndefined, isNullOrUndefinedOrEmpty } from 'utils/collection';

/**
 * An interface for an icon object
 */
export interface IconObject {
  active: string;
  inactive: string;
}

/**
 * An interface for an svg icon object
 */
export interface SvgIconObject {
  active: SVGIconData;
  inactive: SVGIconData;
}

/**
 * An interface for an SVG image
 */
export interface SVGIconData {
  /* the path data for the SVG image */
  data: string;

  /* the height of the image */
  height: number;

  /* the width of the image */
  width: number;
}

/**
 * Describes an icon type.
 * @docs
 * @global
 */
export type Icon = IconObject | string;

/**
 * Describes an icon type.
 * @docs
 * @global
 */
export type SVGIcon = SvgIconObject | SVGIconData;

/**
 * Given an icon object or string (src), this function will return the full object that describes an icon type.
 * That way we can "combine" the two possible inputs (string or obj) and work with objects as a result.
 * @param   {Icon}        src   The icon type
 * @returns {IconObject}        The icon object
 */
export const getIconObject = (src: Icon): IconObject => {
  if (!isIcon(src)) {
    return undefined;
  }
  if (typeof src === 'string') {
    return {
      active: src,
      inactive: src,
    };
  }

  return src;
};

/**
 * Given a single svg icon object or an active-inactive combination, this function will return a full combination.
 * @param   {SVGIcon}        src    The icon type
 * @returns {SvgIconObject}         The icon object
 */
export const getSVGIconObject = (src: SVGIcon): SvgIconObject => {
  if (!isSVGIcon(src)) {
    return undefined;
  }
  if (!isNullOrUndefined((src as SVGIconData)?.data)) {
    return {
      active: src as SVGIconData,
      inactive: src as SVGIconData,
    };
  }
  return src as SvgIconObject;
};

/**
 * Determines if value is of type Icon (Type Guard)
 * @param {Icon | SVGIcon} toBeDetermined The value to check
 * @returns {boolean} True if value is of type "Icon". False otherwise
 */
export const isIcon = (toBeDetermined: Icon | SVGIcon): toBeDetermined is Icon => {
  if (
    !isNullOrUndefinedOrEmpty(toBeDetermined as string) ||
    (!isNullOrUndefinedOrEmpty((toBeDetermined as IconObject)?.active) && !isNullOrUndefinedOrEmpty((toBeDetermined as IconObject)?.inactive))
  ) {
    return true;
  }
  return false;
};

/**
 * Determines if value is of type SVGIcon (Type Guard)
 * @param {Icon | SVGIcon} toBeDetermined The value to check
 * @returns {boolean} True if value is of type "SVGIcon". False otherwise
 */
export const isSVGIcon = (toBeDetermined: Icon | SVGIcon): toBeDetermined is SVGIcon => {
  if (
    !isNullOrUndefinedOrEmpty((toBeDetermined as SVGIconData)?.data) ||
    (!isNullOrUndefinedOrEmpty((toBeDetermined as SvgIconObject)?.active?.data) &&
      !isNullOrUndefinedOrEmpty((toBeDetermined as SvgIconObject)?.inactive?.data))
  ) {
    return true;
  }
  return false;
};
